import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowPathIcon,
  CheckIcon,
  LightBulbIcon,
  ChartBarIcon,
  UserGroupIcon,
  BuildingStorefrontIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { getExistingTopics } from '@/services/blogApi';
import {
  getTopicRecommendations,
  type TopicRecommendation,
  type CoverageStats,
} from '@/utils/topicRecommendationEngine';
import { INDUSTRY_TAXONOMY } from '@/utils/industryTaxonomy';

// Storage key for persisting selections
const STORAGE_KEY = 'blogTopicRecommendations';

interface TopicRecommendationsProps {
  onTopicsSelected: (topics: TopicRecommendation[]) => void;
  selectedCategory: 'consumer' | 'business';
}

// Load persisted state from localStorage
function loadPersistedState(): {
  activeTab: 'consumer' | 'business';
  selectedProfessions: string[];
  expandedIndustry: string | null;
} | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore errors
  }
  return null;
}

// Save state to localStorage
function persistState(state: {
  activeTab: 'consumer' | 'business';
  selectedProfessions: string[];
  expandedIndustry: string | null;
}) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore errors
  }
}

// Clear persisted state
function clearPersistedState() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore errors
  }
}

export default function TopicRecommendations({
  onTopicsSelected,
}: TopicRecommendationsProps) {
  // Load initial state from localStorage
  const persistedState = useMemo(() => loadPersistedState(), []);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'consumer' | 'business'>(
    persistedState?.activeTab || 'consumer'
  );
  const [selectedProfessions, setSelectedProfessions] = useState<Set<string>>(
    new Set(persistedState?.selectedProfessions || [])
  );
  const [hasGenerated, setHasGenerated] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [expandedIndustry, setExpandedIndustry] = useState<string | null>(
    persistedState?.expandedIndustry || null
  );

  // Persist state changes to localStorage
  useEffect(() => {
    persistState({
      activeTab,
      selectedProfessions: Array.from(selectedProfessions),
      expandedIndustry,
    });
  }, [activeTab, selectedProfessions, expandedIndustry]);

  // Fetch existing topics for gap analysis
  const { data: existingTopics, isLoading: isLoadingTopics, error } = useQuery({
    queryKey: ['existingTopics'],
    queryFn: async () => {
      console.log('[TopicRecommendations] Fetching existing topics...');
      const start = performance.now();
      const result = await getExistingTopics();
      console.log(`[TopicRecommendations] Fetched ${result.length} topics in ${(performance.now() - start).toFixed(0)}ms`);
      return result;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Log any errors
  if (error) {
    console.error('[TopicRecommendations] Error fetching topics:', error);
  }

  // Get all professions grouped by industry
  const professionsByIndustry = useMemo(() => {
    const grouped: Record<string, string[]> = {};
    for (const item of INDUSTRY_TAXONOMY) {
      grouped[item.industry] = item.professions
        .filter(p => p.name !== 'Other')
        .map(p => p.name);
    }
    return grouped;
  }, []);

  // Generate recommendations based on gaps and filters
  // For Consumer: Only generate when hasGenerated is true AND professions are selected
  // For Business: Generate immediately (no profession filter needed)
  const { recommendations, stats } = useMemo(() => {
    if (isLoadingTopics) {
      return {
        recommendations: [] as TopicRecommendation[],
        stats: {
          totalProfessions: 0,
          coveredProfessions: 0,
          consumerTopics: 0,
          businessTopics: 0,
          coveragePercentage: 0,
        } as CoverageStats,
      };
    }

    // For consumer tab: require profession selection and explicit generation
    if (activeTab === 'consumer') {
      if (!hasGenerated || selectedProfessions.size === 0) {
        // Return just stats without recommendations
        const result = getTopicRecommendations(existingTopics || [], 0, 'consumer');
        return { recommendations: [], stats: result.stats };
      }
      // Generate topics for selected professions only
      const result = getTopicRecommendations(
        existingTopics || [],
        50,
        'consumer',
        selectedProfessions
      );
      return result;
    }

    // For business tab: generate immediately with general + profession mix
    const result = getTopicRecommendations(existingTopics || [], 24, 'business');
    return result;
  }, [existingTopics, isLoadingTopics, activeTab, selectedProfessions, hasGenerated, refreshKey]);

  const handleGenerateTopics = () => {
    setHasGenerated(true);
    setSelectedIds(new Set());
    setRefreshKey((prev) => prev + 1);
  };

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
    setSelectedIds(new Set());
  };

  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    setSelectedIds(new Set(recommendations.map((r) => r.id)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleUseSelected = () => {
    const selected = recommendations.filter((r) => selectedIds.has(r.id));
    onTopicsSelected(selected);
  };

  const toggleProfession = (profession: string) => {
    const newSelected = new Set(selectedProfessions);
    if (newSelected.has(profession)) {
      newSelected.delete(profession);
    } else {
      newSelected.add(profession);
    }
    setSelectedProfessions(newSelected);
    // Reset generation state when filter changes
    setHasGenerated(false);
    setSelectedIds(new Set());
  };

  const clearProfessionFilter = () => {
    setSelectedProfessions(new Set());
    setExpandedIndustry(null);
    setHasGenerated(false);
    setSelectedIds(new Set());
    // Clear persisted state when user clicks "Clear All"
    clearPersistedState();
  };

  // When switching tabs, keep profession selections but reset topic selections
  const handleTabChange = (tab: 'consumer' | 'business') => {
    setActiveTab(tab);
    setSelectedIds(new Set());
    // Keep selectedProfessions - they persist across tabs
    setHasGenerated(false);
  };

  if (isLoadingTopics) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center gap-3 py-8">
          <ArrowPathIcon className="w-5 h-5 animate-spin text-brand" />
          <span className="text-gray-500">Analyzing content coverage...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center py-8">
          <p className="text-red-600 font-medium">Failed to load topic recommendations</p>
          <p className="text-sm text-gray-500 mt-1">Check the console for details</p>
        </div>
      </div>
    );
  }

  const showRecommendations = activeTab === 'business' || (activeTab === 'consumer' && hasGenerated && selectedProfessions.size > 0);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <LightBulbIcon className="w-5 h-5 text-amber-500" />
          <h2 className="text-lg font-semibold text-gray-900">Topic Recommendations</h2>
        </div>
        {showRecommendations && (
          <button
            onClick={handleRefresh}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </button>
        )}
      </div>

      {/* Coverage Stats */}
      <CoverageStatsBar stats={stats} />

      {/* Tab Switcher */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => handleTabChange('consumer')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
            activeTab === 'consumer'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <UserGroupIcon className="w-5 h-5" />
          Consumer Topics
        </button>
        <button
          onClick={() => handleTabChange('business')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
            activeTab === 'business'
              ? 'bg-green-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <BuildingStorefrontIcon className="w-5 h-5" />
          Business Topics
        </button>
      </div>

      {/* Consumer: Profession Filter + Generate Button */}
      {activeTab === 'consumer' && (
        <>
          <ProfessionFilter
            professionsByIndustry={professionsByIndustry}
            selectedProfessions={selectedProfessions}
            onToggleProfession={toggleProfession}
            onClear={clearProfessionFilter}
            expandedIndustry={expandedIndustry}
            onExpandIndustry={setExpandedIndustry}
          />

          {/* Generate Button - only show when professions are selected but not yet generated */}
          {selectedProfessions.size > 0 && !hasGenerated && (
            <div className="mb-4">
              <button
                onClick={handleGenerateTopics}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                <SparklesIcon className="w-5 h-5" />
                Generate Topics for {selectedProfessions.size} Selected Profession{selectedProfessions.size !== 1 ? 's' : ''}
              </button>
            </div>
          )}

          {/* Prompt to select professions */}
          {selectedProfessions.size === 0 && (
            <div className="text-center py-8 text-gray-500 border-t border-gray-100">
              <UserGroupIcon className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">Select professions to generate topics</p>
              <p className="text-sm mt-1">Choose one or more professions from the filter above</p>
            </div>
          )}
        </>
      )}

      {/* Business: Description */}
      {activeTab === 'business' && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800">
            <strong>Business topics</strong> focus on why referrals matter, being referral-worthy, and the value of network connections.
            CTAs will drive businesses to join Linked By Six so their connections can refer clients to them.
          </p>
        </div>
      )}

      {/* Show recommendations section */}
      {showRecommendations && (
        <>
          {/* Selection controls */}
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <button
                onClick={selectAll}
                className="text-sm text-brand hover:text-brand-dark font-medium"
              >
                Select All
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={deselectAll}
                className="text-sm text-gray-500 hover:text-gray-700 font-medium"
              >
                Deselect All
              </button>
            </div>
            <span className="text-sm text-gray-500">
              {selectedIds.size} of {recommendations.length} selected
            </span>
          </div>

          {/* Recommendations Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-1">
            {recommendations.map((rec) => (
              <RecommendationCard
                key={rec.id}
                recommendation={rec}
                isSelected={selectedIds.has(rec.id)}
                onToggle={() => toggleSelection(rec.id)}
              />
            ))}
          </div>

          {recommendations.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>Great coverage! No significant gaps found for selected professions.</p>
              <p className="text-sm mt-1">Try selecting different professions or refresh.</p>
            </div>
          )}

          {/* Use Selected Button */}
          {selectedIds.size > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <button
                onClick={handleUseSelected}
                className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white rounded-lg transition-colors ${
                  activeTab === 'consumer'
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                <CheckIcon className="w-4 h-4" />
                Use {selectedIds.size} Selected {activeTab === 'consumer' ? 'Consumer' : 'Business'} Topic{selectedIds.size !== 1 ? 's' : ''}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Coverage Stats Component
function CoverageStatsBar({ stats }: { stats: CoverageStats }) {
  return (
    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-2 mb-2">
        <ChartBarIcon className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">Content Coverage</span>
      </div>
      <div className="grid grid-cols-3 gap-4 text-sm">
        <div>
          <span className="text-gray-500">Professions:</span>{' '}
          <span className="font-semibold text-gray-900">
            {stats.coveredProfessions}/{stats.totalProfessions}
          </span>
          <span className="text-gray-400 ml-1">({stats.coveragePercentage}%)</span>
        </div>
        <div>
          <span className="text-gray-500">Consumer:</span>{' '}
          <span className="font-semibold text-blue-600">{stats.consumerTopics}</span>
        </div>
        <div>
          <span className="text-gray-500">Business:</span>{' '}
          <span className="font-semibold text-green-600">{stats.businessTopics}</span>
        </div>
      </div>
      {/* Progress bar */}
      <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-brand transition-all duration-300"
          style={{ width: `${stats.coveragePercentage}%` }}
        />
      </div>
    </div>
  );
}

// Profession Filter Component
function ProfessionFilter({
  professionsByIndustry,
  selectedProfessions,
  onToggleProfession,
  onClear,
  expandedIndustry,
  onExpandIndustry,
}: {
  professionsByIndustry: Record<string, string[]>;
  selectedProfessions: Set<string>;
  onToggleProfession: (profession: string) => void;
  onClear: () => void;
  expandedIndustry: string | null;
  onExpandIndustry: (industry: string | null) => void;
}) {
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">
          Select Professions {selectedProfessions.size > 0 && `(${selectedProfessions.size} selected)`}
        </span>
        {selectedProfessions.size > 0 && (
          <button
            onClick={onClear}
            className="text-xs text-red-600 hover:text-red-700 font-medium"
          >
            Clear All
          </button>
        )}
      </div>

      {/* Selected professions chips */}
      {selectedProfessions.size > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {Array.from(selectedProfessions).map((prof) => (
            <button
              key={prof}
              onClick={() => onToggleProfession(prof)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200"
            >
              {prof}
              <span className="text-blue-500">&times;</span>
            </button>
          ))}
        </div>
      )}

      {/* Industry accordions */}
      <div className="border border-gray-200 rounded-lg max-h-[200px] overflow-y-auto">
        {Object.entries(professionsByIndustry).map(([industry, professions]) => (
          <div key={industry} className="border-b border-gray-100 last:border-b-0">
            <button
              onClick={() => onExpandIndustry(expandedIndustry === industry ? null : industry)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <span>{industry}</span>
              <span className="text-gray-400">
                {expandedIndustry === industry ? '−' : '+'}
              </span>
            </button>
            {expandedIndustry === industry && (
              <div className="px-3 pb-2 flex flex-wrap gap-1.5">
                {professions.map((profession) => (
                  <button
                    key={profession}
                    onClick={() => onToggleProfession(profession)}
                    className={`px-2 py-1 text-xs rounded-full transition-colors ${
                      selectedProfessions.has(profession)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {profession}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Recommendation Card Component
function RecommendationCard({
  recommendation,
  isSelected,
  onToggle,
}: {
  recommendation: TopicRecommendation;
  isSelected: boolean;
  onToggle: () => void;
}) {
  const categoryColors = {
    consumer: 'bg-blue-100 text-blue-700',
    business: 'bg-green-100 text-green-700',
  };

  return (
    <button
      onClick={onToggle}
      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
        isSelected
          ? recommendation.category === 'consumer'
            ? 'border-blue-500 bg-blue-50'
            : 'border-green-500 bg-green-50'
          : 'border-gray-200 hover:border-gray-300 bg-white'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <div
          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
            isSelected
              ? recommendation.category === 'consumer'
                ? 'bg-blue-600 border-blue-600'
                : 'bg-green-600 border-green-600'
              : 'border-gray-300'
          }`}
        >
          {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 line-clamp-2">{recommendation.title}</p>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span
              className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                categoryColors[recommendation.category]
              }`}
            >
              {recommendation.category}
            </span>
            <span className="text-xs text-gray-500">{recommendation.industry}</span>
            {recommendation.profession !== 'Service Providers' && (
              <span className="text-xs text-gray-400">• {recommendation.profession}</span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}
